import { AxiosError } from 'axios';
import {
  FormPage,
  FormPageErrorMessages,
  FormPageResponse,
  ReCaptchaRequest,
  ReCaptchaSetting,
  TrackingSetting,
  FormAuthenticatedData,
  ExternalMeasurementTagSettings,
  FormAccessRestrictionRedirectResponse,
  ApiEndpointSetting,
  FileUploadSetting,
} from 'backend';
import {
  ErrorContent,
  ErrorResponseBody,
  ErrorResponseStatus,
  ERROR_RESPONSE_STATUS,
  MaintenanceErrorResponseBody,
} from 'fetcher';
import { InputData } from 'shared';
import {
  ExecuteReCaptchaResult,
  EXECUTE_RE_CAPTCHA_RESULT_TYPE,
} from '../useReCaptcha';

export const SUBMISSION_RESULT_TYPE = {
  success: 'success',
  failure: 'failure',
  unauthorized: 'unauthorized',
  fileUploadValidationError: 'fileUploadValidationError',
} as const;

const submissionCommonError = {
  title: 'アクセスしようとしたページを表示できませんでした',
  japanese: '送信ができませんでした、再度送信ボタンを押してください',
  english: '',
};

export type SubmissionSuccess = {
  type: typeof SUBMISSION_RESULT_TYPE.success;
  formPage: FormPage;
  inputData: InputData;
  reCaptchaSetting?: ReCaptchaSetting;
  error?: FormPageErrorMessages;
  trackingSetting?: TrackingSetting;
  authentication?: FormAuthenticatedData;
  externalMeasurementTag?: ExternalMeasurementTagSettings;
  apiEndpoint: ApiEndpointSetting;
  fileUploadSetting?: FileUploadSetting;
};

export type SubmissionFailure = {
  type: typeof SUBMISSION_RESULT_TYPE.failure;
  status: ErrorResponseStatus;
  error: ErrorContent;
};

export type SubmissionUnauthorized = {
  type: typeof SUBMISSION_RESULT_TYPE.unauthorized;
  formPage: FormPage;
  reCaptchaSetting?: ReCaptchaSetting;
  error?: FormPageErrorMessages;
  trackingSetting?: TrackingSetting;
  externalMeasurementTag?: ExternalMeasurementTagSettings;
  apiEndpoint: ApiEndpointSetting;
  fileUploadSetting?: FileUploadSetting;
};

export type SubmissionFileUploadValidationError = {
  type: typeof SUBMISSION_RESULT_TYPE.fileUploadValidationError;
  errorMessage: string;
  unrecoverable: boolean;
};

export type SubmissionAuthResult =
  | SubmissionSuccess
  | SubmissionFailure
  | SubmissionUnauthorized;

export type SubmissionResult =
  | SubmissionSuccess
  | SubmissionFailure
  | SubmissionUnauthorized
  | SubmissionFileUploadValidationError;

const getSubmissionFailure = (
  status: SubmissionFailure['status'],
  error: SubmissionFailure['error'],
): SubmissionFailure => ({
  type: SUBMISSION_RESULT_TYPE.failure,
  status,
  error,
});

export const getReCaptchaRequestPayload = (
  reCaptchaResult?: ExecuteReCaptchaResult,
): ReCaptchaRequest | undefined => {
  if (
    !reCaptchaResult ||
    reCaptchaResult.type === EXECUTE_RE_CAPTCHA_RESULT_TYPE.notUsed
  ) {
    return;
  }
  return {
    version: 'v3',
    token: reCaptchaResult.token,
  } as const;
};

export const handleCommonSubmitError = (
  e: unknown,
): SubmissionFailure | SubmissionUnauthorized => {
  const {
    serverError,
    unauthorized,
    notFound,
    badRequest,
    maintenance,
    forbidden,
  } = ERROR_RESPONSE_STATUS;

  const error = e as AxiosError<unknown>;

  if (error.response?.data == null || typeof error.response.data !== 'object') {
    return getSubmissionFailure(serverError, submissionCommonError);
  }
  const { status } = error.response;

  if (status === forbidden) {
    const data = error.response.data as FormAccessRestrictionRedirectResponse;
    window.location.assign(data.redirectUrl);
  }

  if (status === unauthorized) {
    const data = error.response.data as FormPageResponse;
    const {
      page,
      reCaptcha,
      error: errorMessages,
      trackingSetting,
      externalMeasurementTag,
      apiEndpoint,
      fileUpload: fileUploadSetting,
    } = data;
    return {
      type: SUBMISSION_RESULT_TYPE.unauthorized,
      formPage: page,
      reCaptchaSetting: reCaptcha,
      error: errorMessages,
      trackingSetting,
      externalMeasurementTag,
      apiEndpoint,
      fileUploadSetting,
    };
  }

  const errorContent =
    (error.response.data as ErrorResponseBody | MaintenanceErrorResponseBody)
      .error ?? submissionCommonError;

  switch (status) {
    case notFound:
    case maintenance:
    case badRequest:
      return getSubmissionFailure(status, errorContent);
    default:
      return getSubmissionFailure(serverError, errorContent);
  }
};
